import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import {
  uploadFOIAGuidance,
  searchFOIARules,
  getFOIASources,
  getFOIAExemptions,
  deleteFOIARule,
  deleteFOIARulesBulk,
  deleteFOIARulesBySource
} from '../services/api';
import {
  DocumentArrowUpIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import RejectedFOIARulesReview from '../components/RejectedFOIARulesReview';

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, type = 'danger' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${
            type === 'danger' ? 'bg-red-100' : 'bg-yellow-100'
          }`}>
            <ExclamationTriangleIcon className={`h-6 w-6 ${
              type === 'danger' ? 'text-red-600' : 'text-yellow-600'
            }`} />
          </div>
          <div className="mt-5 text-center">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <div className="mt-2 px-7 py-3">
              <p className="text-sm text-gray-500">{message}</p>
            </div>
            <div className="flex gap-3 justify-center mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  type === 'danger' 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                }`}
              >
                {type === 'danger' ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FOIAPolicy() {
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExemption, setSelectedExemption] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedRules, setSelectedRules] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', data: null });
  const pageSize = 20;

  // State for rejected rules review (dual-track extraction)
  const [rejectedRules, setRejectedRules] = useState([]);
  const [ruleConflicts, setRuleConflicts] = useState([]);
  const [uploadedSourceFile, setUploadedSourceFile] = useState(null);

  const queryClient = useQueryClient();

  // Query for FOIA rules with search and pagination
  const { data: rulesData, isLoading: rulesLoading, error: rulesError } = useQuery({
    queryKey: ['foiaRules', searchQuery, selectedExemption, selectedSource, currentPage],
    queryFn: () => searchFOIARules(searchQuery, selectedExemption, selectedSource, pageSize, currentPage * pageSize),
    staleTime: 30000
  });

  // Query for available sources
  const { data: sourcesData } = useQuery({
    queryKey: ['foiaSources'],
    queryFn: getFOIASources,
    staleTime: 5 * 60 * 1000
  });

  // Query for available exemption categories
  const { data: exemptionsData } = useQuery({
    queryKey: ['foiaExemptions'],
    queryFn: getFOIAExemptions,
    staleTime: 5 * 60 * 1000
  });

  // Mutations
  const deleteSingleRuleMutation = useMutation({
    mutationFn: deleteFOIARule,
    onSuccess: () => {
      queryClient.invalidateQueries(['foiaRules']);
      queryClient.invalidateQueries(['foiaSources']);
      setSelectedRules(new Set());
    }
  });

  const deleteBulkRulesMutation = useMutation({
    mutationFn: deleteFOIARulesBulk,
    onSuccess: () => {
      queryClient.invalidateQueries(['foiaRules']);
      queryClient.invalidateQueries(['foiaSources']);
      setSelectedRules(new Set());
    }
  });

  const deleteBySourceMutation = useMutation({
    mutationFn: deleteFOIARulesBySource,
    onSuccess: () => {
      queryClient.invalidateQueries(['foiaRules']);
      queryClient.invalidateQueries(['foiaSources']);
      setSelectedRules(new Set());
    }
  });

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadError(null);
    setUploadSuccess(null);
    setRejectedRules([]);
    setRuleConflicts([]);
    setUploadedSourceFile(null);

    try {
      const response = await uploadFOIAGuidance(file);

      // Handle dual-track response (valid + rejected rules)
      const validCount = response.total_valid_rules || response.rules_indexed || 0;
      const rejectedCount = response.total_rejected_rules || 0;
      const conflictCount = response.total_conflicts || 0;

      // Build success message
      let successMsg = `Successfully uploaded ${file.name}. `;
      successMsg += `${validCount} FOIA rule(s) indexed.`;

      if (rejectedCount > 0) {
        successMsg += ` ${rejectedCount} rule(s) need review.`;
      }
      if (conflictCount > 0) {
        successMsg += ` ${conflictCount} potential conflict(s) detected.`;
      }

      setUploadSuccess(successMsg);
      setUploadedSourceFile(file.name);

      // Set rejected rules for review if any
      if (response.rejected_rules && response.rejected_rules.length > 0) {
        setRejectedRules(response.rejected_rules);
      }

      // Set conflicts if any
      if (response.conflicts && response.conflicts.length > 0) {
        setRuleConflicts(response.conflicts);
      }

      queryClient.invalidateQueries(['foiaRules']);
      queryClient.invalidateQueries(['foiaSources']);
      queryClient.invalidateQueries(['foiaExemptions']);
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Failed to upload FOIA guidance document');
    }
  };

  // Handler to close rejected rules review
  const handleCloseRejectedReview = () => {
    setRejectedRules([]);
    setRuleConflicts([]);
    setUploadedSourceFile(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false
  });

  const handleSelectRule = (ruleId) => {
    const newSelected = new Set(selectedRules);
    if (newSelected.has(ruleId)) {
      newSelected.delete(ruleId);
    } else {
      newSelected.add(ruleId);
    }
    setSelectedRules(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRules.size === rulesData?.rules?.length) {
      setSelectedRules(new Set());
    } else {
      setSelectedRules(new Set(rulesData?.rules?.map(rule => rule.rule_id || rule.id) || []));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRules.size === 0) return;
    
    setConfirmDialog({
      isOpen: true,
      type: 'bulk',
      data: Array.from(selectedRules),
      title: 'Delete Selected Rules',
      message: `Are you sure you want to delete ${selectedRules.size} selected FOIA rule(s)? This action cannot be undone.`
    });
  };

  const handleDeleteBySource = (sourceFile) => {
    setConfirmDialog({
      isOpen: true,
      type: 'source',
      data: sourceFile,
      title: 'Delete All Rules from Source',
      message: `Are you sure you want to delete all FOIA rules from "${sourceFile}"? This action cannot be undone.`
    });
  };

  const handleDeleteSingle = (ruleId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'single',
      data: ruleId,
      title: 'Delete Rule',
      message: 'Are you sure you want to delete this FOIA rule? This action cannot be undone.'
    });
  };

  const confirmDelete = () => {
    const { type, data } = confirmDialog;
    
    switch (type) {
      case 'single':
        deleteSingleRuleMutation.mutate(data);
        break;
      case 'bulk':
        deleteBulkRulesMutation.mutate(data);
        break;
      case 'source':
        deleteBySourceMutation.mutate(data);
        break;
    }
    
    setConfirmDialog({ isOpen: false, type: '', data: null });
  };

  const getExemptionBadgeColor = (exemption) => {
    const colors = {
      'b1': 'bg-red-100 text-red-800',
      'b2': 'bg-orange-100 text-orange-800', 
      'b3': 'bg-yellow-100 text-yellow-800',
      'b4': 'bg-green-100 text-green-800',
      'b5': 'bg-blue-100 text-blue-800',
      'b6': 'bg-indigo-100 text-indigo-800',
      'b7': 'bg-purple-100 text-purple-800'
    };
    return colors[exemption.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const rules = rulesData?.rules || [];
  const totalRules = rulesData?.total || 0;
  const totalPages = Math.ceil(totalRules / pageSize);
  const sources = sourcesData?.sources || [];
  const exemptions = exemptionsData?.exemptions || [];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">FOIA Policy Management</h1>
        <p className="text-gray-600">
          Manage FOIA exemption rules and guidance documents for processing Freedom of Information Act requests.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload Panel */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
              Upload FOIA Guidance
            </h2>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <DocumentArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {isDragActive
                  ? 'Drop the document here...'
                  : 'Drag & drop FOIA guidance here, or click to select'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOC, DOCX, TXT files
              </p>
            </div>

            {uploadSuccess && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                  <p className="text-sm text-green-700">{uploadSuccess}</p>
                </div>
              </div>
            )}

            {uploadError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-700">{uploadError}</p>
                </div>
              </div>
            )}
          </div>

          {/* Rejected Rules Review Component */}
          {rejectedRules.length > 0 && (
            <RejectedFOIARulesReview
              rejectedRules={rejectedRules}
              conflicts={ruleConflicts}
              onClose={handleCloseRejectedReview}
              sourceFile={uploadedSourceFile}
            />
          )}

          {/* Source Management */}
          {sources && sources.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Source Documents</h3>
              <div className="space-y-2">
                {sources.map((source) => (
                  <div key={source.source} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{source.source}</p>
                      <p className="text-xs text-gray-500">{source.count} rules</p>
                    </div>
                    <button
                      onClick={() => handleDeleteBySource(source.source)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete all rules from this source"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Rules Management Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
              <h2 className="text-lg font-semibold">Search FOIA Rules</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Search rules..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <select
                  value={selectedExemption}
                  onChange={(e) => {
                    setSelectedExemption(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Exemptions</option>
                  {exemptions && exemptions.map((exemption) => (
                    <option key={exemption} value={exemption}>
                      {exemption}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={selectedSource}
                  onChange={(e) => {
                    setSelectedSource(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Sources</option>
                  {sources && sources.map((source) => (
                    <option key={source.source} value={source.source}>
                      {source.source}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          {rules.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedRules.size === rules.length && rules.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Select All ({selectedRules.size} selected)
                    </span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  {selectedRules.size > 0 && (
                    <button
                      onClick={handleDeleteSelected}
                      className="bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 text-sm flex items-center"
                    >
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Delete Selected
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rules List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {rulesLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading FOIA rules...</p>
              </div>
            ) : rulesError ? (
              <div className="p-6 text-center text-red-600">
                Error loading FOIA rules: {rulesError.message}
              </div>
            ) : rules.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2">No FOIA rules found</p>
                <p className="text-sm">Upload FOIA guidance documents to get started</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {rules.map((rule) => {
                  
                  return (
                    <div key={rule.rule_id || rule.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedRules.has(rule.rule_id || rule.id)}
                            onChange={() => handleSelectRule(rule.rule_id || rule.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <p className="text-sm font-medium text-blue-900">{rule.rule_id || rule.id}</p>
                                {rule.exemption_category && (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExemptionBadgeColor(rule.exemption_category.replace(/[()]/g, ''))}`}>
                                    {rule.exemption_category}
                                  </span>
                                )}
                                {rule.context_domain && (
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                    {rule.context_domain}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleDeleteSingle(rule.rule_id || rule.id)}
                                className="text-red-600 hover:text-red-800 p-1 ml-2"
                                title="Delete this rule"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Rule Title */}
                            {rule.rule_title && (
                              <h4 className="text-sm font-medium text-gray-900 mb-2">{rule.rule_title}</h4>
                            )}
                            
                            {/* Description */}
                            {(rule.rule_description || rule.description) && (
                              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                {rule.rule_description || rule.description}
                              </p>
                            )}
                            
                            {/* Keywords */}
                            {rule.keywords && rule.keywords.length > 0 && (
                              <div className="mb-3">
                                <span className="text-xs font-medium text-gray-700">Keywords:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {rule.keywords.slice(0, 8).map((keyword, keywordIndex) => (
                                    <span
                                      key={keywordIndex}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                    >
                                      {keyword}
                                    </span>
                                  ))}
                                  {rule.keywords.length > 8 && (
                                    <span className="text-xs text-gray-500 px-2 py-1">
                                      +{rule.keywords.length - 8} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Examples */}
                            {rule.examples && (
                              <div className="mb-3">
                                <span className="text-xs font-medium text-gray-700">Examples:</span>
                                <p className="text-xs text-gray-600 mt-1 bg-yellow-50 border border-yellow-200 rounded p-2">
                                  {rule.examples}
                                </p>
                              </div>
                            )}
                            
                            {/* Agency Guidance */}
                            {rule.agency_guidance && (
                              <div className="mb-3">
                                <span className="text-xs font-medium text-gray-700">Agency Guidance:</span>
                                <p className="text-xs text-gray-600 mt-1 bg-blue-50 border border-blue-200 rounded p-2">
                                  {rule.agency_guidance}
                                </p>
                              </div>
                            )}
                            
                            {/* Source and Context */}
                            <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
                              <div className="flex items-center space-x-4">
                                {rule.source && <span>Source: {rule.source}</span>}
                                {rule.context_section && <span>Section: {rule.context_section}</span>}
                              </div>
                              {rule.legal_citation && rule.legal_citation !== 'N/A' && (
                                <span className="text-blue-600">Citation: {rule.legal_citation}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalRules)} of {totalRules} rules
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, type: '', data: null })}
        onConfirm={confirmDelete}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
}